import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminTable from 'common/AdminTable';
import axiosInstance from 'apis/axiosConfig';

const ViewLoanProvider = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [provider, setProvider] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`loan-providers/${id}`);
            if (res.data) {
                setProvider(res.data.provider);
                setUsers(res.data.users || []);
            }
        } catch (err) {
            console.error("Error fetching provider details:", err);
            toast.error("Failed to load Loan Provider ledger details.");
            navigate('/loan-providers');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchDetails();
        }
    }, [id]);

    const handleDeleteUser = async (userId) => {
        if (window.confirm("Are you sure you want to permanently delete this customer ledger record?")) {
            try {
                await axiosInstance.delete(`customer/${userId}`);
                toast.success("Borrower profile record deleted successfully!");
                fetchDetails();
            } catch (err) {
                console.error("Delete failed:", err);
                toast.error("Failed to delete borrower profile record.");
            }
        }
    };

    const handleActionClick = (row) => {
        navigate(`/users/update/${row.id}`);
    };

    const tableHeaders = [
        { Header: 'ID', accessor: 'id', Cell: ({ row }) => <strong style={{ color: '#0284C7' }}>#{row.original.id}</strong> },
        { 
            Header: 'Borrower Name', 
            accessor: 'name', 
            Cell: ({ row }) => (
                <div className="text-left font-weight-bold" style={{ color: '#0F172A', fontSize: '15px' }}>
                    {row.original.name ? `${row.original.name} ${row.original.last_name || ''}`.trim() : row.original.account_holder_name || 'Borrower Profile'}
                </div>
            ) 
        },
        { 
            Header: 'User Type', 
            accessor: 'user_type', 
            Cell: ({ row }) => {
                const isInvestor = row.original.user_type === 'investor';
                return (
                    <span 
                        className="badge py-1 px-2 font-weight-bold" 
                        style={{ 
                            borderRadius: '6px', 
                            backgroundColor: isInvestor ? '#F3E8FF' : '#E2F1F1', 
                            color: isInvestor ? '#7E22CE' : '#082032', 
                            border: `1px solid ${isInvestor ? '#E9D5FF' : '#BAE6FD'}` 
                        }}
                    >
                        {isInvestor ? 'Investor' : 'User'}
                    </span>
                );
            }
        },
        { Header: 'Mobile Contact', accessor: 'mobile', Cell: ({ row }) => <span style={{ fontWeight: '600', color: '#334155' }}>{row.original.mobile || 'N/A'}</span> },
        { Header: 'PAN Identity', accessor: 'pan_number', Cell: ({ row }) => <span className="text-uppercase font-weight-bold" style={{ color: '#475569', letterSpacing: '0.5px' }}>{row.original.pan_number || 'N/A'}</span> },
        { Header: 'Bank Account/No', accessor: 'bank_account_number', Cell: ({ row }) => <span style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: '700' }}>{row.original.bank_account_number || 'N/A'}</span> },
        { 
            Header: 'Audit Status', 
            accessor: 'is_active', 
            Cell: ({ row }) => {
                const isActive = row.original.is_active === 1 || row.original.is_active === true;
                return (
                    <span 
                        className="badge py-2 px-3 font-weight-bold" 
                        style={{ 
                            borderRadius: '20px', 
                            fontSize: '12px',
                            backgroundColor: isActive ? '#D1FAE5' : '#FEF3C7',
                            color: isActive ? '#065F46' : '#B45309',
                            border: `1px solid ${isActive ? '#A7F3D0' : '#FDE68A'}`
                        }}
                    >
                        {isActive ? '✓ Active & Verified' : '⏳ Pending Approval'}
                    </span>
                );
            } 
        },
        { 
            Header: 'Onboarded Date', 
            accessor: 'createdAt', 
            Cell: ({ row }) => {
                const date = row.original.createdAt ? new Date(row.original.createdAt).toLocaleDateString() : 'Recent';
                return <span style={{ fontSize: '13px', color: '#64748B', fontWeight: '600' }}>{date}</span>;
            }
        },
        { 
            Header: 'Actions', 
            accessor: 'action', 
            Cell: ({ row }) => (
                <div className="d-flex align-items-center">
                    <button 
                        type="button"
                        className="btn btn-sm font-weight-bold shadow-sm mr-2" 
                        style={{ backgroundColor: '#E2F1F1', color: '#082032', borderRadius: '8px', padding: '6px 12px', border: 0 }}
                        onClick={() => handleActionClick(row.original)}
                    >
                        🔍 View
                    </button>
                    <button 
                        type="button"
                        className="btn btn-sm font-weight-bold shadow-sm" 
                        style={{ backgroundColor: '#FEE2E2', color: '#DC2626', borderRadius: '8px', padding: '6px 12px', border: 0 }}
                        onClick={() => handleDeleteUser(row.original.id)}
                    >
                        🗑️ Delete
                    </button>
                </div>
            ) 
        }
    ];

    const totalUsers = users.filter(u => u.user_type !== 'investor').length;
    const totalInvestors = users.filter(u => u.user_type === 'investor').length;

    return (
        <div className="container-fluid mt-4 mb-5" style={{ fontFamily: "'Inter', 'Roboto', sans-serif" }}>
            {/* Back button & Header */}
            <div className="mb-4 px-2 d-flex justify-content-between align-items-center">
                <div>
                    <button 
                        className="btn btn-sm btn-outline-secondary font-weight-bold mb-3 shadow-sm px-3"
                        onClick={() => navigate('/loan-providers')}
                        style={{ borderRadius: '8px', fontSize: '12px' }}
                    >
                        ← Back to Providers
                    </button>
                    <h2 className="font-weight-bold mb-1 text-dark">Provider Ledgers Audit</h2>
                    <p className="mb-0 text-muted" style={{ fontSize: '14px' }}>Inspect client sub-accounts onboarded under this provider&apos;s credentials.</p>
                </div>
                <button 
                    className="btn btn-outline-primary font-weight-bold px-3 py-1 shadow-sm"
                    onClick={fetchDetails}
                    disabled={loading}
                    style={{ borderRadius: '8px', fontSize: '13px' }}
                >
                    {loading ? 'Refreshing...' : '🔄 Force Sync'}
                </button>
            </div>

            {loading && !provider ? (
                <div className="text-center py-5 my-5">
                    <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                    <h5 className="font-weight-bold mt-3 text-secondary">Loading Ledger Pipeline...</h5>
                </div>
            ) : (
                <>
                    {/* Provider Summary Cards */}
                    <div className="row mb-4">
                        {/* Profile Info */}
                        <div className="col-xl-4 col-md-6 mb-3">
                            <div className="card border-0 shadow-sm bg-white p-4 h-100" style={{ borderRadius: '20px', borderLeft: '6px solid #7E22CE' }}>
                                <span style={{ fontSize: '11px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Provider Identity</span>
                                <h4 className="font-weight-bold text-dark mt-2 mb-1">{provider?.name}</h4>
                                <p className="text-muted font-weight-bold mb-3" style={{ fontSize: '14px' }}>{provider?.email}</p>
                                <div>
                                    <span 
                                        className="badge font-weight-bold py-1 px-3"
                                        style={{
                                            borderRadius: '20px',
                                            backgroundColor: (provider?.status === true || provider?.status === 1) ? '#D1FAE5' : '#FEE2E2',
                                            color: (provider?.status === true || provider?.status === 1) ? '#065F46' : '#991B1B'
                                        }}
                                    >
                                        {(provider?.status === true || provider?.status === 1) ? 'Active' : 'Suspended'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Onboarded Users */}
                        <div className="col-xl-4 col-md-6 mb-3">
                            <div className="card border-0 shadow-sm bg-white p-4 h-100" style={{ borderRadius: '20px', borderLeft: '6px solid #0EA5A4' }}>
                                <span style={{ fontSize: '11px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Onboarded Borrowers</span>
                                <h2 className="font-weight-bold mt-3 mb-0" style={{ color: '#0F172A', fontSize: '32px' }}>{totalUsers}</h2>
                                <p className="text-muted mb-0 font-weight-bold mt-1" style={{ fontSize: '12px' }}>Active User Accounts</p>
                            </div>
                        </div>

                        {/* Onboarded Investors */}
                        <div className="col-xl-4 col-md-6 mb-3">
                            <div className="card border-0 shadow-sm bg-white p-4 h-100" style={{ borderRadius: '20px', borderLeft: '6px solid #10B981' }}>
                                <span style={{ fontSize: '11px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Onboarded Investors</span>
                                <h2 className="font-weight-bold mt-3 mb-0" style={{ color: '#0F172A', fontSize: '32px' }}>{totalInvestors}</h2>
                                <p className="text-muted mb-0 font-weight-bold mt-1" style={{ fontSize: '12px' }}>Active Investor Accounts</p>
                            </div>
                        </div>
                    </div>

                    {/* Table List of Associated Clients */}
                    <div className="card border-0 shadow-sm bg-white p-4" style={{ borderRadius: '20px' }}>
                        <h4 className="font-weight-bold text-dark mb-4">Associated Borrower Ledger</h4>
                        {users.length > 0 ? (
                            <div className="animate__animated animate__fadeIn">
                                <AdminTable tableHeaders={tableHeaders} tableData={users} />
                            </div>
                        ) : (
                            <div className="text-center py-5 my-4 bg-light rounded-lg border">
                                <span style={{ fontSize: '40px' }}>📭</span>
                                <h5 className="font-weight-bold mt-2 text-dark">No Client Accounts Found</h5>
                                <p className="text-muted mb-0">No users or investors have been onboarded by this Loan Provider yet.</p>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ViewLoanProvider;

